import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Shield, CheckCircle2, XCircle, Sparkles, Trash2, AlertTriangle,
  Cloud, MessageSquare, ScrollText, KeyRound, CreditCard, Mail, Phone,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { dateTime, humanise, money, relative } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, Checkbox, ConfirmDialog, DetailRow, EmptyState, Field,
  Input, LoadingBlock, Modal, MoneyInput, PageHeader, Select, Tabs, Textarea, useToast, statusTone, Avatar,
} from '@/components/ui';
import type {
  Org, User, StaffRole, PublicConfig, SendingIdentity, TenantDocumentKind,
  SubscriptionState, CancellationTerms, PlatformStatus, Jurisdiction,
} from '@shared/types';
import { COUNTRIES } from '@/lib/countries';
import { DataExport } from '@/components/DataExport';
import { DataImport } from '@/components/DataImport';
import { LogoSettings } from '@/components/OrgLogo';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'KES', 'NGN', 'ZAR', 'GHS', 'UGX', 'TZS', 'INR', 'AUD', 'CAD'];
const ROLES: { value: StaffRole; label: string; hint: string }[] = [
  { value: 'owner', label: 'Owner', hint: 'Full control, including staff accounts and org settings' },
  { value: 'manager', label: 'Manager', hint: 'Can verify payments, grant extensions and end leases' },
  { value: 'staff', label: 'Staff', hint: 'Can record data, but payments they enter go to review' },
  { value: 'viewer', label: 'Viewer', hint: 'Read-only access' },
];

interface SettingsResponse {
  org: Org;
  plan: { key: string; property_limit: number; properties_used: number };
  /** Capability flags only — never a supplier, a key or a value. */
  features: { documents: boolean; messaging: boolean; email: boolean };
  /** The addresses tenants see, and where their replies land. */
  sender: SendingIdentity;
  required_tenant_documents: TenantDocumentKind[];
  /** The local wording and prompts, so no screen has to assume a country. */
  jurisdiction?: Jurisdiction;
}

/**
 * The paperwork a landlord can insist on holding for every tenant.
 *
 * Deliberately a choice rather than a fixed list: what is required differs by
 * country and by how somebody works. PROVIA tracks what you have said you
 * need — it does not know, and does not claim to know, what the law where you
 * let actually requires.
 */
const REQUIRABLE_DOCUMENTS: { kind: TenantDocumentKind; label: string; hint: string }[] = [
  { kind: 'id_document', label: 'Photo ID', hint: 'Passport, driving licence or national ID' },
  { kind: 'contract', label: 'Signed agreement', hint: 'Your copy of the tenancy agreement' },
  { kind: 'proof_of_address', label: 'Proof of address', hint: 'A recent bill or statement' },
  { kind: 'right_to_rent', label: 'Right to rent / immigration check', hint: 'Where your country requires one' },
  { kind: 'reference', label: 'Reference', hint: 'From a previous landlord or an employer' },
  { kind: 'guarantor', label: 'Guarantor agreement', hint: 'Where somebody is standing surety' },
];

export default function SettingsPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState('organisation');

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Organisation, staff accounts and your plan" />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'organisation', label: 'Organisation' },
          { id: 'plan', label: 'Plan' },
          ...(can('manager') ? [{ id: 'staff', label: 'Staff' }] : []),
          { id: 'data', label: 'Your data' },
          { id: 'account', label: 'My account' },
          ...(can('manager') ? [{ id: 'audit', label: 'Audit log' }] : []),
        ]}
      />

      {tab === 'organisation' && <OrganisationTab />}
      {tab === 'plan' && <PlanTab />}
      {tab === 'staff' && can('manager') && <StaffTab />}
      {tab === 'data' && (
        <div className="space-y-6">
          <DataImport />
          <DataExport />
        </div>
      )}
      {tab === 'account' && <AccountTab />}
      {tab === 'audit' && can('manager') && <AuditTab />}
    </div>
  );
}

interface DeletionPreview {
  organisation: string;
  counts: {
    properties: number; rooms: number; tenants: number; leases: number;
    charges: number; payments: number; bills: number; documents: number;
    messages: number; maintenance: number; staff_accounts: number;
  };
}

function OrganisationTab() {
  const { can, refresh } = useAuth();
  const toast = useToast();
  const { data, loading, reload } = useApi<SettingsResponse>('/settings');
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: '', currency: 'USD', timezone: 'UTC', grace_days: 3, country: '',
    address: '', contact_email: '', contact_phone: '',
    payment_instructions: '', payment_reference_note: '',
    org_type: 'individual', legal_name: '', company_number: '',
  });
  const [autoConfirm, setAutoConfirm] = useState(false);
  const [lateFeeCents, setLateFeeCents] = useState(0);
  const [requiredDocs, setRequiredDocs] = useState<TenantDocumentKind[]>([]);

  // Hydrate the form once the org loads.
  useEffect(() => {
    if (!data?.org) return;
    setForm({
      name: data.org.name,
      currency: data.org.currency,
      timezone: data.org.timezone,
      country: data.org.country ?? '',
      grace_days: data.org.grace_days,
      address: data.org.address ?? '',
      contact_email: data.org.contact_email ?? '',
      contact_phone: data.org.contact_phone ?? '',
      payment_instructions: data.org.payment_instructions ?? '',
      payment_reference_note: data.org.payment_reference_note ?? '',
      org_type: data.org.org_type ?? 'individual',
      legal_name: data.org.legal_name ?? '',
      company_number: data.org.company_number ?? '',
    });
    setAutoConfirm(Boolean(data.org.auto_confirm_bank_matches));
    setLateFeeCents(data.org.late_fee_cents);
    setRequiredDocs(data.required_tenant_documents ?? []);
  }, [data?.org, data?.required_tenant_documents]);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function save() {
    setBusy(true);
    try {
      await api.patch('/settings', {
        ...form,
        grace_days: Number(form.grace_days),
        late_fee_cents: lateFeeCents,
        required_tenant_documents: requiredDocs,
        auto_confirm_bank_matches: autoConfirm,
      });
      toast.success('Settings saved');
      await refresh();
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save settings');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingBlock rows={5} />;
  if (!data) return null;

  return (
    <>
    <Card>
      <CardHeader
        title="Organisation"
        subtitle="Applies across every property and tenant"
        action={can('owner') && (
          <Button variant="primary" onClick={() => void save()} loading={busy}>Save changes</Button>
        )}
      />
      <div className="p-5 sm:p-6 pt-4 space-y-5 max-w-2xl">
        <Field label="Name">
          <Input value={form.name} onChange={set('name')} disabled={!can('owner')} />
        </Field>

        <Field label="Logo">
          <LogoSettings canEdit={can('owner')} />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Currency" hint="Changing this does not convert existing amounts">
            <Select value={form.currency} onChange={set('currency')} disabled={!can('owner')}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Timezone">
            <Input value={form.timezone} onChange={set('timezone')} disabled={!can('owner')} placeholder="Africa/Nairobi" />
          </Field>
        </div>

        {/*
          Where they let. PROVIA is used well outside England, and this is what
          stops it talking as though it were not: it decides whether a screen
          says "deposit" or "bond", and whether there is any local rule to
          mention at all. Left blank, the app stays deliberately general rather
          than guessing from the currency — a wrong guess about a legal deadline
          is worse than no guess. See 36_Jurisdictions.gs.
        */}
        <Field
          label="Where you let"
          hint="Sets the local wording and any rules we can point you at. You are still responsible for the law where you let."
        >
          <Select value={form.country} onChange={set('country')} disabled={!can('owner')}>
            <option value="">Not set — keep the wording general</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </Select>
        </Field>

        {data.jurisdiction && (
          <p className="text-sm text-muted leading-relaxed -mt-2">
            {data.jurisdiction.deposit_note}
            {data.jurisdiction.authority_url ? (
              <>
                {' '}
                <a
                  className="link"
                  href={data.jurisdiction.authority_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {data.jurisdiction.authority ?? 'Read more'}
                </a>
              </>
            ) : data.jurisdiction.authority ? ` ${data.jurisdiction.authority}.` : ''}
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field
            label="Grace period (days)"
            hint="How long after the due date before rent is flagged as overdue in the reminder queue"
          >
            <Input
              type="number"
              min={0}
              max={60}
              value={form.grace_days}
              onChange={set('grace_days')}
              disabled={!can('owner')}
            />
          </Field>
          <Field label="Default late fee" hint="Suggested when you add a late fee. Never applied automatically.">
            <MoneyInput
              valueCents={lateFeeCents}
              onChangeCents={setLateFeeCents}
              currency={form.currency}
              disabled={!can('owner')}
            />
          </Field>
        </div>

        {/*
          A person or a company. Not cosmetic: a company has a registered
          number that belongs on correspondence, and a person has none and
          should never be asked for one.
        */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="You are" hint="Most landlords let in their own name.">
            <Select value={form.org_type} onChange={set('org_type')} disabled={!can('owner')}>
              <option value="individual">An individual</option>
              <option value="company">A company</option>
            </Select>
          </Field>
          <Field
            label={form.org_type === 'company' ? 'Registered name' : 'Your full legal name'}
            hint={form.org_type === 'company'
              ? 'If it differs from the name above'
              : 'If the name above is a trading name'}
          >
            <Input value={form.legal_name} onChange={set('legal_name')} disabled={!can('owner')} />
          </Field>
        </div>

        {form.org_type === 'company' && (
          <Field label="Company number" hint="Goes on correspondence with your tenants.">
            <Input
              value={form.company_number}
              onChange={set('company_number')}
              disabled={!can('owner')}
              placeholder="12345678"
            />
          </Field>
        )}

        <Field label="Address">
          <Input value={form.address} onChange={set('address')} disabled={!can('owner')} />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field
            label="Contact email"
            required
            hint="Where replies from your tenants are delivered. Required."
          >
            <Input type="email" value={form.contact_email} onChange={set('contact_email')} disabled={!can('owner')} />
          </Field>
          <Field label="Contact phone">
            <Input value={form.contact_phone} onChange={set('contact_phone')} disabled={!can('owner')} placeholder="+254700000000" />
          </Field>
        </div>

        <SendingIdentityPanel sender={data.sender} features={data.features} canRequest={can('owner')} />

        {/*
          What this landlord expects to hold on every tenant. Anyone missing
          one is flagged on the reminder screen and sees the same checklist in
          their own account.
        */}
        <div className="pt-2 border-t border-line space-y-4">
          <div>
            <h3 className="font-medium text-ink">Documents you need from every tenant</h3>
            <p className="text-sm text-muted mt-1 leading-relaxed">
              Anyone with a live tenancy who is missing one of these is flagged
              on the reminder screen, and sees the same checklist in their own
              account so they can upload it themselves. It is your list — PROVIA
              does not know what the law where you let requires, and ticking
              these is not advice that they are enough.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {REQUIRABLE_DOCUMENTS.map((doc) => (
              <Checkbox
                key={doc.kind}
                checked={requiredDocs.includes(doc.kind)}
                disabled={!can('owner')}
                onChange={(e) => setRequiredDocs((current) => (
                  e.target.checked
                    ? [...current, doc.kind]
                    : current.filter((k) => k !== doc.kind)
                ))}
                label={doc.label}
                description={doc.hint}
              />
            ))}
          </div>
        </div>

        {/*
          Shown to tenants in their portal so they know where to send rent.
          PROVIA never handles the money — collecting rent on a landlord's
          behalf would make us a handler of client money, which carries its own
          licensing. This is the landlord's own account, in their own words.
        */}
        <div className="pt-2 border-t border-line space-y-4">
          <div>
            <h3 className="font-medium text-ink">How your tenants pay you</h3>
            <p className="text-sm text-muted mt-1 leading-relaxed">
              Shown to every tenant in their portal, alongside what they owe and when
              it is due. Money goes straight to you — it never passes through PROVIA.
            </p>
          </div>

          <Field
            label="Payment details"
            hint="Whatever your tenants need: bank name, BSB and account, sort code, IBAN."
          >
            <Textarea
              rows={4}
              value={form.payment_instructions}
              onChange={set('payment_instructions')}
              disabled={!can('owner')}
              placeholder={'Bank: Example Bank\nBSB: 062-000\nAccount: 1234 5678'}
            />
          </Field>

          {/*
            The one place automation is allowed near the ledger, and it is off
            until asked for. The reasoning is in 34_Banking.gs: a statement is
            the landlord's own bank telling them money arrived, which is the
            same fact they would be confirming by hand.
          */}
          <Checkbox
            checked={autoConfirm}
            disabled={!can('owner')}
            onChange={(e) => setAutoConfirm(e.target.checked)}
            label="Record exact matches from an imported statement automatically"
            description="Only when the reference and the amount both match exactly. A short payment, or one with no reference, always waits for you."
          />

          <Field label="Reference to use" hint="How you tell one tenant's transfer from another's.">
            <Input
              value={form.payment_reference_note}
              onChange={set('payment_reference_note')}
              disabled={!can('owner')}
              placeholder="Use your room number as the reference"
            />
          </Field>
        </div>

        {!can('owner') && (
          <p className="text-sm text-muted bg-raised border border-line rounded-xl px-3 py-2.5">
            Only an owner can change organisation settings.
          </p>
        )}
      </div>
    </Card>

    {can('owner') && <DangerZone />}
    </>
  );
}

/**
 * Closing the account.
 *
 * The one irreversible action in the system, so it is built to be hard to do
 * by accident and impossible to do vaguely: the real counts are fetched and
 * shown first, then the organisation's name has to be typed and the current
 * password given. Nothing is greyed-out-then-enabled by a checkbox; the
 * confirm button simply will not fire until both match.
 */
function DangerZone() {
  const toast = useToast();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: preview } = useApi<DeletionPreview>(open ? '/organisation/deletion-preview' : null);

  const nameMatches = preview
    ? confirmName.trim().toLowerCase() === preview.organisation.trim().toLowerCase()
    : false;

  async function run() {
    setBusy(true);
    try {
      await api.post('/organisation/delete', { confirm_name: confirmName, password });
      toast.success('Your organisation and all of its data have been deleted');
      signOut();
      navigate('/');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the organisation');
    } finally {
      setBusy(false);
    }
  }

  const rows: [string, number | undefined][] = [
    ['Properties', preview?.counts.properties],
    ['Rooms', preview?.counts.rooms],
    ['Tenants', preview?.counts.tenants],
    ['Leases', preview?.counts.leases],
    ['Charges', preview?.counts.charges],
    ['Payments', preview?.counts.payments],
    ['Bills', preview?.counts.bills],
    ['Documents', preview?.counts.documents],
    ['Messages', preview?.counts.messages],
    ['Maintenance requests', preview?.counts.maintenance],
    ['Staff accounts', preview?.counts.staff_accounts],
  ];

  return (
    <>
      <Card className="border-danger/30">
        <CardHeader
          title={
            <span className="flex items-center gap-2 text-danger">
              <AlertTriangle className="h-5 w-5" /> Close this account
            </span>
          }
          subtitle="Deletes your organisation and everything in it"
        />
        <div className="p-5 sm:p-6 pt-4 space-y-4">
          <p className="text-sm text-muted leading-relaxed">
            Every property, tenant, lease, payment record and stored document is
            permanently removed. Tenants lose access to their portal immediately.
            This cannot be undone, and we cannot recover it for you afterwards.
          </p>
          <p className="text-sm text-muted leading-relaxed">
            If you only want to stop being billed, get in touch instead — closing
            the account is not the same as cancelling a subscription.
          </p>
          <Button variant="danger" onClick={() => setOpen(true)}>
            <Trash2 className="h-4 w-4" /> Delete organisation
          </Button>
        </div>
      </Card>

      <Modal
        open={open}
        onClose={() => { setOpen(false); setConfirmName(''); setPassword(''); }}
        title="Delete this organisation"
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-danger/30 bg-danger/8 p-4">
            <p className="text-sm font-semibold text-ink">This will permanently delete:</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              {rows.map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-muted">{label}</dt>
                  <dd className="text-ink font-medium text-right tabular-nums">
                    {value ?? '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <Field
            label={`Type "${preview?.organisation ?? ''}" to confirm`}
            hint="Case does not matter."
          >
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={preview?.organisation ?? ''}
              autoComplete="off"
            />
          </Field>

          <Field label="Your password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={run}
              disabled={!nameMatches || password.length === 0 || busy}
              loading={busy}
            >
              Delete everything
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * What this account can do, and what it costs.
 *
 * Deliberately says nothing about how any of it is implemented. A customer
 * needs to know whether document storage and messaging are switched on, and
 * what their plan allows. Which suppliers sit behind those capabilities is
 * not their concern and not their decision, so naming them here would only
 * invite questions nobody benefits from answering.
 */
function FeatureStrip() {
  const { data } = useApi<SettingsResponse>('/settings');
  const f = data?.features;

  const rows: [typeof Cloud, string, string, boolean][] = [
    [Cloud, 'PROVIA Storage', 'Photos, contracts, IDs and receipts', Boolean(f?.documents)],
    [MessageSquare, 'Text messaging', 'Reminders, receipts and tenant sign-in codes', Boolean(f?.messaging)],
    [Mail, 'Email', 'Sign-in help and account notices for your staff', Boolean(f?.email)],
  ];

  return (
    <Card>
      <CardHeader title="What's switched on" subtitle="Included with every plan" />
      <div className="p-5 sm:p-6 pt-4 space-y-3">
        {rows.map(([Icon, title, blurb, on]) => (
          <div key={title} className="flex items-start gap-3">
            <Icon className="h-4 w-4 shrink-0 mt-0.5 text-faint" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{title}</p>
              <p className="text-sm text-muted">{blurb}</p>
            </div>
            {on
              ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-ok" />
              : <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-faint" />}
          </div>
        ))}
        {/*
          The one place a supplier-adjacent detail belongs: these two are what
          a tenant sees in their phone and a member of staff sees in their
          inbox, and "what number will this arrive from?" is a question a
          manager should not have to send a text to answer. Read-only — both
          are set by us, not by the customer.
        */}
        {(data?.sender?.sms_from || data?.sender?.email_from) && (
          <div className="pt-3 border-t border-line space-y-2">
            {data.sender?.sms_from && (
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 shrink-0 mt-0.5 text-faint" />
                <p className="text-sm text-muted">
                  Your texts come from{' '}
                  <span className="text-ink font-medium">{data.sender.sms_from}</span>
                </p>
              </div>
            )}
            {data.sender?.email_from && (
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 shrink-0 mt-0.5 text-faint" />
                <p className="text-sm text-muted">
                  Your email comes from{' '}
                  <span className="text-ink font-medium">{data.sender.email_from}</span>
                  {!data.sender.email_personalised && ' — the shared address'}
                </p>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-muted pt-2 border-t border-line leading-relaxed">
          Anything showing as off has not been enabled on your account yet — everything
          else keeps working. Get in touch and we will switch it on.
        </p>
      </div>
    </Card>
  );
}

/** The shape is declared once, in shared/types.ts, and used on both sides. */
type Subscription = SubscriptionState;

/**
 * The plan, and how to pay for it.
 *
 * Payment happens on the provider's own hosted page, reached by the button
 * below — this application never sees a card number or a bank account, and
 * never stores one. That is not squeamishness: taking a card number into our
 * own form would put the business into full PCI-DSS scope, and those numbers
 * would end up somewhere they must never be. The hosted page also handles the
 * country differences for free, card or bank debit, without any of it being
 * our problem.
 */
function PlanTab() {
  const toast = useToast();
  const { data: sub, loading, reload } = useApi<Subscription>('/subscription');
  const { data: pub } = useApi<PublicConfig>('/public/config');
  const [busy, setBusy] = useState(false);

  if (loading) return <LoadingBlock rows={4} />;
  if (!sub) return null;

  const published = pub?.pricing.plans.find((p) => p.key === sub.plan);
  const limit = sub.property_limit;
  const used = sub.properties_used;
  const atLimit = limit > 0 && used >= limit;

  const tone = sub.is_demo ? 'brand'
    : !sub.active ? 'danger'
    : sub.status === 'trialing' ? 'warn'
    : 'ok';

  async function pay(planKey: string) {
    setBusy(true);
    try {
      const res = await api.post<{ checkout_url: string | null; message?: string; contact?: string }>(
        '/subscription/checkout', { plan: planKey },
      );
      if (res.checkout_url) {
        window.open(res.checkout_url, '_blank', 'noopener,noreferrer');
      } else {
        toast.info(res.message ?? `Get in touch at ${res.contact ?? sub!.sales_email}`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not open the payment page');
    } finally {
      setBusy(false);
      reload();
    }
  }

  return (
    <div className="space-y-6">
      {/* State of the account, said plainly and first. */}
      <Card className={!sub.active ? 'border-danger/40' : undefined}>
        <CardHeader
          title={<span className="flex items-center gap-2"><Sparkles className="h-5 w-5" /> Your plan</span>}
          subtitle={published?.name ?? sub.plan}
          action={
            <Badge tone={tone as any} dot>
              {sub.is_demo ? 'Demo'
                : sub.status === 'active' ? 'Active'
                : sub.status === 'trialing' ? `Trial — ${sub.trial_days_left} day${sub.trial_days_left === 1 ? '' : 's'} left`
                : 'Blocked'}
            </Badge>
          }
        />
        <div className="p-5 sm:p-6 pt-4 space-y-4">
          {!sub.active && (
            <div className="rounded-xl border border-danger/30 bg-danger/8 p-4 text-sm">
              <p className="font-semibold text-ink">This organisation is blocked.</p>
              <p className="text-muted mt-1 leading-relaxed">
                The trial ended and no payment has been received, so nothing new can be
                saved. <strong className="text-ink">Nothing has been deleted.</strong> Every
                property, tenant and payment record is still here, and you can still read
                and export it. Paying below unblocks it immediately.
              </p>
            </div>
          )}

          {sub.active && sub.status === 'trialing' && (
            <div className="rounded-xl border border-warn/30 bg-warn/8 p-4 text-sm">
              <p className="font-semibold text-ink">
                {sub.trial_days_left} day{sub.trial_days_left === 1 ? '' : 's'} left on your trial
              </p>
              <p className="text-muted mt-1 leading-relaxed">
                After that the organisation is blocked until payment is received. Your data
                stays exactly where it is either way.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-2xl font-semibold text-ink tracking-tight">
              {published?.price_display ?? 'Agreed with you'}
            </span>
            {published?.price_display && <span className="text-sm text-muted">per month</span>}
          </div>

          <div>
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted">Properties</span>
              <span className="text-ink font-medium">
                {used}{limit > 0 ? ` of ${limit}` : ''}
              </span>
            </div>
            {limit > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-raised overflow-hidden">
                <div
                  className={`h-full rounded-full ${atLimit ? 'bg-warn' : 'bg-brand'}`}
                  style={{ width: `${Math.min(100, Math.round((used / limit) * 100))}%` }}
                />
              </div>
            )}
          </div>

          <p className="text-sm text-muted leading-relaxed border-t border-line pt-4">
            Text messages are included. There is no per-message charge and no overage
            bill, however many you send.
          </p>
        </div>
      </Card>

      {/* What is switched on for this account. Names no supplier: a customer
          needs to know whether documents and messaging work, not who runs
          them. */}
      <FeatureStrip />

      {/* Choosing and paying. */}
      {!sub.is_demo && (
        <Card>
          <CardHeader
            title={<span className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Activate or change plan</span>}
            subtitle="Payment opens on our provider's secure page"
          />
          <div className="p-5 sm:p-6 pt-4 space-y-3">
            {(pub?.pricing.plans ?? []).map((p) => {
              const current = p.key === sub.plan;
              return (
                <div
                  key={p.key}
                  className={`flex flex-wrap items-center gap-3 rounded-xl border p-4 ${
                    current ? 'border-brand/50 bg-brand/5' : 'border-line'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ink">
                      {p.name}
                      {current && <span className="ml-2 text-xs text-brand font-normal">Current</span>}
                    </p>
                    <p className="text-sm text-muted mt-0.5">
                      {p.price_display ? `${p.price_display} per month · ` : ''}
                      {p.property_limit
                        ? `up to ${p.property_limit} properties`
                        : 'more than 10 properties, priced with you'}
                    </p>
                  </div>
                  <Button
                    variant={current && !sub.active ? 'primary' : 'secondary'}
                    onClick={() => void pay(p.key)}
                    loading={busy}
                  >
                    {p.price_display ? 'Pay' : 'Enquire'}
                  </Button>
                </div>
              );
            })}

            <div className="rounded-xl bg-raised border border-line p-4 text-sm text-muted leading-relaxed">
              <p className="text-ink font-medium">How payment works</p>
              <p className="mt-1">
                Choosing a plan opens our payment provider&rsquo;s own secure page. You can
                pay by card, or by bank debit where your country supports it — BSB and
                account number in Australia, or a UK account for Direct Debit.
              </p>
              <p className="mt-2">
                Those details are entered on their page and never pass through PROVIA. We
                do not see them, and we never store them. Your account unblocks as soon as
                the payment clears — if it has not within an hour, email{' '}
                <a href={`mailto:${sub.billing_email}`} className="text-brand hover:underline">
                  {sub.billing_email}
                </a>.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/*
        Last on the tab, and never hidden. Somebody looking for how to leave
        should find it where they expect to, not have to email and ask.
      */}
      <CancelPlanPanel sub={sub} onOpened={reload} />
    </div>
  );
}

function StaffTab() {
  const { can, staff } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [deactivating, setDeactivating] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, reload } = useApi<User[]>('/users');
  const users = data ?? [];

  async function deactivate() {
    if (!deactivating) return;
    setBusy(true);
    try {
      await api.del(`/users/${deactivating.id}`);
      toast.success('Account deactivated');
      setDeactivating(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not deactivate that account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Staff accounts"
          subtitle="Roles decide who can verify payments and change money"
          action={can('owner') && (
            <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
              Add a user
            </Button>
          )}
        />
        <div className="p-5 sm:p-6 pt-4">
          {loading ? (
            <LoadingBlock rows={3} />
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40">
                  <Avatar name={u.full_name} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">
                      {u.full_name}
                      {u.id === staff?.id && <span className="text-faint font-normal"> · you</span>}
                    </p>
                    <p className="text-xs text-faint truncate">{u.email}</p>
                  </div>
                  <div className="hidden sm:block text-xs text-faint shrink-0">
                    {u.last_login_at ? `Last in ${relative(u.last_login_at)}` : 'Never signed in'}
                  </div>
                  <Badge tone={u.role === 'owner' ? 'brand' : u.role === 'manager' ? 'accent' : 'neutral'}>
                    {humanise(u.role)}
                  </Badge>
                  {!u.is_active && <Badge tone="danger">Inactive</Badge>}
                  {can('owner') && u.id !== staff?.id && (
                    <Button size="sm" variant="outline" onClick={() => setEditing(u)}>Edit</Button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-line">
            <p className="label mb-3">What each role can do</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {ROLES.map((r) => (
                <div key={r.value} className="flex items-start gap-2.5 text-sm">
                  <Shield className="h-4 w-4 text-faint shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{r.label}</p>
                    <p className="text-xs text-muted mt-0.5 leading-relaxed">{r.hint}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {editing && (
        <UserModal
          user={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
          onDeactivate={(u) => { setEditing(null); setDeactivating(u); }}
        />
      )}

      <ConfirmDialog
        open={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={() => void deactivate()}
        loading={busy}
        title="Deactivate this account?"
        confirmLabel="Deactivate"
        message="They will be signed out and unable to sign back in. Their history stays on record, and you can reactivate them later."
      />
    </>
  );
}

function UserModal({ user, onClose, onDone, onDeactivate }: {
  user?: User; onClose: () => void; onDone: () => void; onDeactivate: (u: User) => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [role, setRole] = useState<StaffRole>(user?.role ?? 'staff');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(user?.is_active ?? true);

  async function submit() {
    if (!fullName.trim()) { toast.error('Enter their name'); return; }
    if (!user && !email.trim()) { toast.error('Enter an email address'); return; }
    if (!user && password.length < 10) { toast.error('Set a password of at least 10 characters'); return; }

    setBusy(true);
    try {
      if (user) {
        await api.patch(`/users/${user.id}`, {
          full_name: fullName.trim(),
          phone: phone || null,
          role,
          is_active: isActive,
          ...(password ? { password } : {}),
        });
      } else {
        await api.post('/users', {
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone || null,
          role,
          password,
        });
      }
      toast.success(user ? 'Account updated' : 'Account created');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={user ? 'Edit account' : 'Add a user'}
      footer={
        <>
          {user && user.is_active && (
            <Button variant="ghost" onClick={() => onDeactivate(user)} disabled={busy}>Deactivate</Button>
          )}
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Full name" required>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
        </Field>

        <Field label="Email" required={!user} hint={user ? 'Email cannot be changed' : undefined}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!!user}
          />
        </Field>

        <Field label="Phone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+254700000000" />
        </Field>

        <Field label="Role" hint={ROLES.find((r) => r.value === role)?.hint}>
          <Select value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>
        </Field>

        <Field
          label={user ? 'Reset password' : 'Password'}
          hint={user ? 'Leave blank to keep the current one' : 'At least 10 characters'}
          required={!user}
        >
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>

        {user && (
          <Checkbox
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            label="Account is active"
          />
        )}
      </div>
    </Modal>
  );
}

function AccountTab() {
  const { staff } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  async function changePassword() {
    if (next.length < 10) { toast.error('New password must be at least 10 characters'); return; }
    if (next !== confirm) { toast.error('The new passwords do not match'); return; }

    setBusy(true);
    try {
      await api.post('/auth/staff/change-password', { current_password: current, new_password: next });
      toast.success('Password changed');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not change your password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader title="Your account" />
        <div className="p-5 sm:p-6 pt-4">
          <dl>
            <DetailRow label="Name">{staff?.full_name}</DetailRow>
            <DetailRow label="Email">{staff?.email}</DetailRow>
            <DetailRow label="Role">{humanise(staff?.role ?? '')}</DetailRow>
            <DetailRow label="Organisation">{staff?.org.name}</DetailRow>
          </dl>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Change password</span>}
        />
        <div className="p-5 sm:p-6 pt-4 space-y-4">
          <Field label="Current password">
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          </Field>
          <Field label="New password" hint="At least 10 characters">
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          </Field>
          <Field label="Confirm new password">
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </Field>
          <Button
            variant="primary"
            onClick={() => void changePassword()}
            loading={busy}
            disabled={!current || !next}
          >
            Change password
          </Button>
        </div>
      </Card>
    </div>
  );
}

function AuditTab() {
  const { currency } = useAuth();
  const [entity, setEntity] = useState('');
  const { data, loading } = useApi<any[]>('/audit', { entity: entity || undefined, limit: 200 });
  const entries = data ?? [];

  return (
    <Card>
      <CardHeader
        title="Audit log"
        subtitle="Every payment verification, extension and waiver, with who did it"
        action={
          <Select value={entity} onChange={(e) => setEntity(e.target.value)} className="w-auto min-w-[150px]">
            <option value="">Everything</option>
            {['payment', 'charge', 'lease', 'tenant', 'property', 'bill', 'message', 'user', 'file']
              .map((e) => <option key={e} value={e}>{humanise(e)}</option>)}
          </Select>
        }
      />
      <div className="p-5 sm:p-6 pt-4">
        {loading ? (
          <LoadingBlock rows={6} />
        ) : entries.length === 0 ? (
          <EmptyState icon={<ScrollText className="h-6 w-6" />} title="Nothing recorded yet" />
        ) : (
          <div className="space-y-1">
            {entries.map((e) => (
              <div key={e.id} className="flex items-start gap-3 py-2.5 border-b border-line/50 last:border-0">
                <Badge tone={statusTone(e.action)} className="shrink-0 min-w-[100px] justify-center">
                  {humanise(e.action)}
                </Badge>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="text-ink">
                    <span className="font-medium">{e.actor_label ?? 'System'}</span>
                    <span className="text-muted"> · {humanise(e.entity)}</span>
                  </p>
                  {e.detail && Object.keys(e.detail).length > 0 && (
                    <p className="text-xs text-faint mt-0.5 truncate">
                      {Object.entries(e.detail)
                        .map(([k, v]) => k.endsWith('_cents')
                          ? `${humanise(k.replace('_cents', ''))}: ${money(Number(v), currency)}`
                          : `${humanise(k)}: ${String(v)}`)
                        .join(' · ')}
                    </p>
                  )}
                </div>
                <span className="text-xs text-faint shrink-0 whitespace-nowrap">{dateTime(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}


/**
 * The two addresses a tenant sees, and where their replies land.
 *
 * ── Why it is read-only ───────────────────────────────────────────────────
 *
 * Both are assigned by whoever runs PROVIA. A sending address has to exist on
 * a domain that is already authenticated with the mail provider, and a phone
 * number has to be bought — neither is something a customer can type into a
 * box and have work. What the customer does control is where replies go, and
 * that is simply their contact email above.
 *
 * Showing it matters anyway. "What will my tenants see this come from, and
 * what happens if they reply?" is the first question anybody asks about
 * software that emails on their behalf, and it deserves an answer on the page
 * rather than in a support thread.
 */
function SendingIdentityPanel({ sender, features, canRequest }: {
  sender: SendingIdentity;
  features: { documents: boolean; messaging: boolean; email: boolean };
  canRequest: boolean;
}) {
  /*
   * Asked for separately, because they are two different decisions. A landlord
   * may well want their own sending address — it costs us nothing and their
   * tenants see their name on it — while being perfectly content texting from
   * the shared number, or the other way round. One button for both would
   * record somebody as having asked for something they did not want.
   */
  const { data: platform, reload } = useApi<PlatformStatus>(canRequest ? '/platform/status' : null);

  return (
    <div className="pt-2 border-t border-line space-y-4">
      <div>
        <h3 className="font-medium text-ink">What your tenants see, and where replies go</h3>
        <p className="text-sm text-muted mt-1 leading-relaxed">
          Messages go out in your organisation&rsquo;s name. Pressing reply
          writes to your contact address above — nothing is forwarded, because
          the sending address has no mailbox behind it.
        </p>
      </div>

      <div className="rounded-2xl border border-line divide-y divide-line">
        <div className="p-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm text-muted w-28 shrink-0">Email from</span>
          <span className="font-mono text-sm text-ink break-all">
            {features.email
              ? (sender.email_from ?? 'being set up')
              : 'email is not switched on yet'}
          </span>
        </div>
        <div className="p-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm text-muted w-28 shrink-0">Replies go to</span>
          <span className="font-mono text-sm text-ink break-all">
            {sender.reply_to ?? 'nowhere — add a contact email above'}
          </span>
          {/*
            Never imply a reply will find its way back when it will not. Some
            mail providers have no reply-to header at all, and on those the
            address is printed in the message instead.
          */}
          {sender.reply_to && !sender.reply_to_header && (
            <span className="w-full text-xs text-muted">
              Your provider cannot attach a reply-to address, so it is written
              into the message itself — tenants are told to write to you there.
            </span>
          )}
        </div>
        <div className="p-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm text-muted w-28 shrink-0">Texts from</span>
          <span className="font-mono text-sm text-ink break-all">
            {!features.messaging ? 'texting is not switched on yet'
              : sender.sms_from ?? 'a shared number'}
          </span>
        </div>
      </div>

      {canRequest && platform && (
        <div className="grid sm:grid-cols-2 gap-3">
          <RequestCard
            kind="email_address"
            title="An email address of your own"
            have={Boolean(sender.email_personalised)}
            haveLabel="Your tenants already see your own address."
            pending={platform.awaiting_email_address}
            available={platform.can_request_email_address}
            unavailable="Email is not switched on for this deployment yet."
            why="Mail would come from your own name at our domain instead of the
              shared one. Replies still come to you either way."
            onDone={reload}
          />
          <RequestCard
            kind="sms_number"
            title="A phone number of your own"
            have={Boolean(sender.sms_from)}
            haveLabel="Your tenants already text a number that is yours."
            pending={platform.awaiting_sms_number}
            available={platform.can_request_sms_number}
            unavailable="Texting is not switched on for this deployment yet."
            why="Tenants could reply to your texts and reach you. On the shared
              number they cannot, and we tell them so in the message."
            /*
              Said before they ask, not after we have bought one. A landlord in
              Brisbane assumes a British company will hand them a British
              number; finding out afterwards that their tenants are being
              texted from +44 arrives as a complaint rather than a question.
              And where they have not told us the country, this asks instead of
              guessing — the whole point of the jurisdiction picker.
            */
            /*
              Optional throughout, because the frontend deploys to Netlify and
              the backend is a separate Apps Script deployment — so for a while
              after any release this page is talking to a build that has never
              heard of `sms_region`. A missing field has to read as "we do not
              know yet", not as a blank screen on somebody's settings.
            */
            note={platform.sms_region?.country_name
              ? `We will assign a number suited to your region — ${
                platform.sms_region.country_name}${
                platform.sms_region.dialling_code
                  ? ` (${platform.sms_region.dialling_code})`
                  : ''}, because that is where you have told us you let.`
              : 'We assign a number suited to your region. Set the country you '
                + 'let in above first, so your tenants are not texted from '
                + 'another country\u2019s number.'}
            onDone={reload}
          />
        </div>
      )}

      {!sender.provisioned && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-info/10 border border-info/25">
          <Sparkles className="h-5 w-5 text-info shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed">
            We are still setting part of this up for you. Everything else works
            in the meantime, and we will let you know when it is done.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * One request, for one thing.
 *
 * Four states, and each says something different: you already have it, we are
 * working on it, you can ask, or there is nothing to ask for because the
 * deployment does not do that yet. The last one matters — a button that raises
 * a request nobody can act on is a promise we cannot keep.
 */
function RequestCard({
  kind, title, why, have, haveLabel, pending, available, unavailable, note, onDone,
}: {
  kind: 'sms_number' | 'email_address';
  title: string;
  why: string;
  have: boolean;
  haveLabel: string;
  pending: boolean;
  available: boolean;
  unavailable: string;
  /** What we would do for them, shown before they ask rather than after. */
  note?: string;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function request() {
    setBusy(true);
    try {
      const res = await api.post<{ requested: boolean; already_requested?: boolean }>(
        '/platform/request', { kind },
      );
      toast.success(res.requested
        ? 'Asked for. We will email you when it is set up.'
        : 'Already on our list — we are working on it.');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send that request');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-line p-4 space-y-2">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted leading-relaxed">
        {have ? haveLabel : why}
      </p>
      {!have && note && (
        <p className="text-xs text-info leading-relaxed">{note}</p>
      )}
      {have ? (
        <Badge tone="ok">Set up</Badge>
      ) : pending ? (
        <Badge tone="info">Requested — we are on it</Badge>
      ) : available ? (
        <Button size="sm" onClick={() => void request()} loading={busy}>Request</Button>
      ) : (
        <p className="text-xs text-faint">{unavailable}</p>
      )}
    </div>
  );
}

/**
 * Cancelling, and what it actually costs you.
 *
 * ── Why the button opens somebody else's website ──────────────────────────
 *
 * Cancelling is an irreversible money decision, and it belongs on the payment
 * provider's own page — with their confirmation step and their emailed receipt
 * — rather than on a screen written by the company being cancelled. It also
 * means we never see a card number.
 *
 * ── Why the consequences are spelled out before the button ────────────────
 *
 * Because hiding them is a dark pattern, and because it does not even work:
 * people cancel anyway and then write to ask what they lost. Saying it up
 * front costs one paragraph and saves the conversation.
 */
function CancelPlanPanel({ sub, onOpened }: {
  sub: SubscriptionState;
  onOpened?: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState<CancellationTerms>(sub.cancellation);

  if (sub.is_demo) return null;

  async function openBilling() {
    setBusy(true);
    try {
      const res = await api.post<{
        portal_url: string | null; message?: string; contact?: string;
        cancellation: CancellationTerms;
      }>('/subscription/portal');
      setTerms(res.cancellation);
      if (res.portal_url) {
        // A new tab, not this one: coming back to a half-read settings page is
        // better than losing it.
        window.open(res.portal_url, '_blank', 'noopener');
        onOpened?.();
      } else {
        toast.info(res.message ?? `Email ${res.contact ?? 'us'} and we will sort it out.`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'The billing page could not be opened');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={terms.already_cancelling ? 'Your plan is ending' : 'Cancelling'}
        subtitle={
          terms.already_cancelling && terms.access_until_label
            ? `Set to stop on ${terms.access_until_label}. You can restart it before then.`
            : terms.on_trial
              ? 'You are on a trial, so there is nothing to cancel — it simply stops.'
              : 'What happens, before you decide.'
        }
      />
      <div className="p-5 sm:p-6 pt-4 space-y-4">
        <ul className="space-y-2">
          {terms.points.map((point, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-muted leading-relaxed">
              <span className="text-faint shrink-0" aria-hidden>—</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>

        {!terms.on_trial && (
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={() => void openBilling()} loading={busy}>
              {sub.can_manage_billing ? 'Open billing and cancel' : 'How to cancel'}
            </Button>
            <p className="text-xs text-faint">
              {sub.can_manage_billing
                ? 'Opens our payment provider in a new tab. Change your card or download invoices there too.'
                : 'This account was not set up through online payment, so we cancel it for you.'}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
